import React, {useRef} from 'react';
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Typography} from '@mui/material';
import PrintableSubmission from './PrintableSubmission';
import {SubmissionPDFContent} from './export-pdf';
import {PDFDownloadLink} from '@react-pdf/renderer';
import {useReactToPrint} from 'react-to-print';
import {Close, Download, Print} from "@mui/icons-material";
import {Form, FormSubmission} from "crqlar-react-common/dist/models/forms/form";
import {useQuery} from "react-query";
import {FormService} from "crqlar-react-common/dist/services/forms/form.service";
import {useTranslation} from "react-i18next";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface SubmissionDetailDialogProps {
    open: boolean;
    onClose: () => void;
    submission: FormSubmission;
}

export default function SubmissionDetailDialog({
                                               open,
                                               onClose,
                                               submission,
                                           }: SubmissionDetailDialogProps) {
    const {t} = useTranslation();
    // Read-only users may view/print a submission but must not download (export) its data.
    const readOnly = useReadOnly();

    // Fetch form data to get questions
    const formQuery = useQuery({
        queryKey: ['form', submission.formId],
        queryFn: async () => {
            if (!submission.formId) throw new Error('Form ID is required');
            return await FormService.getFormById(submission.formId);
        },
        enabled: !!submission.formId
    });
    const form = formQuery.data

    // Generate filename for PDF download
    const getPDFFilename = () => {
        const date = submission.insertDate
            ? submission.insertDate.toISOString().split('T')[0]
            : 'unknown-date';
        return `form-submission-${submission.id}-${date}.pdf`;
    };

    const printRef = useRef<HTMLDivElement>(null);
    const reactToPrintFn = useReactToPrint({
        contentRef: printRef,
        documentTitle: getPDFFilename(),
        pageStyle: `
              @page {
                size: A4;
                margin: 20mm;
              }
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            `
    });


    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    height: '90vh',
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5">{t('Submission Details')}</Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close/>
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                <PrintableSubmission ref={printRef} submission={submission} form={form}/>
            </DialogContent>

            <DialogActions sx={{px: 3, py: 2}}>
                <Button
                    variant="outlined"
                    startIcon={<Print/>}
                    onClick={() => reactToPrintFn()}
                >
                    {t('Print')}
                </Button>

                <Stack direction="row" spacing={2}>
                    {readOnly ? (
                        <Button
                            variant="contained"
                            startIcon={<Download/>}
                            disabled
                        >
                            {t('Download PDF')}
                        </Button>
                    ) : (
                        <PDFDownloadLink
                            document={<SubmissionPDFContent submission={submission} form={form}/>}
                            fileName={getPDFFilename()}
                            style={{textDecoration: 'none'}}
                        >
                            {({loading}) => (
                                <Button
                                    variant="contained"
                                    startIcon={<Download/>}
                                    disabled={loading}
                                >
                                    {loading ? t('Generating PDF...') : t('Download PDF')}
                                </Button>
                            )}
                        </PDFDownloadLink>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
}
